import React from "react";
import { Box, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export const INK = "#333";
export const MUTED = "#6b7280";
export const GRID = "#e3e6eb";
export const CARD = {
  border: "1px solid #e0e0e0",
  borderRadius: "10px",
};

// Reserved status colors (dataviz palette); always paired with an icon + label.
export const VERDICTS = {
  better: { label: "Better", icon: "✓", color: "#0ca30c" },
  worse: { label: "Worse", icon: "✕", color: "#d03b3b" },
  no_clear_change: { label: "No clear change", icon: "–", color: "#8a9099" },
  not_enough_data: { label: "Not enough data", icon: "…", color: "#b8bdc6" },
};

export const fmtValue = (v, unit) => {
  if (v == null) return "–";
  if (unit === "%") return `${v.toFixed(1)}%`;
  if (unit === "min") return `${v.toFixed(1)} min`;
  return v >= 10 ? v.toFixed(0) : v.toFixed(1);
};

export const fmtLift = (v) => (v == null ? "–" : `${v > 0 ? "+" : ""}${v.toFixed(1)}%`);

export const Verdict = ({ verdict }) => {
  const v = VERDICTS[verdict] || VERDICTS.not_enough_data;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        fontSize: "12.5px",
        color: INK,
        whiteSpace: "nowrap",
      }}
    >
      <Box
        component="span"
        sx={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: v.color,
          color: "#fff",
          fontSize: "11px",
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {v.icon}
      </Box>
      {v.label}
    </Box>
  );
};


const seconds = (ms) => `${(ms ?? 0) / 1000}s`;

/**
 * One-line meaning of each metric, matching the backend
 * (feedPersonalizationMetrics.helper.js); thresholds from the live config.
 */
export const metricDefinitions = (config = {}) => ({
  videosPerDay: "Avg videos watched per day the user opened the feed.",
  votesPerDay: "Avg votes per day the user opened the feed.",
  minutesPerSession: "Avg active minutes per session (each video counts up to 60s). 30+ min away starts a new session.",
  videosPerSession: "Avg videos watched per session.",
  votesPerSession: "Avg votes per session.",
  voteRate: "% of videos watched that got a vote.",
  sessionsPerDay: "Avg sessions per day the user opened the feed.",
  nextDayReturn: "% of active days followed by a session the next day.",
  quickSkipRate: `% of videos left in under ${seconds(config.quickSkipMs)} without a vote.`,
  bounceRate: "% of sessions with fewer than 3 videos.",
  categoriesPerSession: "Avg categories seen per session. Lower = narrower feeds.",
  lowRankLiveShare: "% of open-match views on Unranked to Bronze. Lower = new creators seen less.",
});

/** Signal meanings, only where the label alone isn't enough. */
export const signalDefinitions = (config = {}) => ({
  profileView: "Opened a contestant's profile.",
  replay: "Replayed a video.",
  vote: "Voted. Votes needed to fill energy don't count.",
  longWatch: `Watched ${Math.round((config.watchThroughShare ?? 0.8) * 100)}%+ of a video.`,
  quickSkip: `Left in under ${seconds(config.quickSkipMs)} without voting.`,
  leftOnCard: "Closed the app or left the feed on a match.",
});

const NAVY_TIP = "#142C5C";

/** Small (i) icon that shows `text` on hover. */
export const InfoTip = ({ text }) =>
  text ? (
    <Tooltip title={text} arrow placement="top">
      <Box
        component="span"
        tabIndex={0}
        aria-label={text}
        sx={{
          display: "inline-flex",
          verticalAlign: "middle",
          ml: 0.5,
          color: "#9aa1ab",
          cursor: "help",
          "&:hover, &:focus": { color: NAVY_TIP },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
      </Box>
    </Tooltip>
  ) : null;

// Validated categorical slots (dataviz palette), fixed per group so a
// group keeps its color across runs.
const VARIANT_COLORS = {
  personalized: "#2a78d6",
  fast: "#eb6834",
  slow: "#1baf7a",
};
const FALLBACK_COLORS = ["#2a78d6", "#eb6834", "#1baf7a"];

/** The run's personalized groups with display colors. */
export const variantsWithColors = (variants = []) =>
  (variants.length ? variants : [{ key: "personalized", label: "Normal" }]).map(
    (v, i) => ({ ...v, color: VARIANT_COLORS[v.key] || FALLBACK_COLORS[i % 3] })
  );

/** A verdict is clear when the interval excludes zero. */
export const isClear = (verdict) => verdict === "better" || verdict === "worse";

/** Small verdict dot (icon only), for tight multi-group cells. */
export const VerdictDot = ({ verdict, title }) => {
  const v = VERDICTS[verdict] || VERDICTS.not_enough_data;
  return (
    <Box
      component="span"
      title={title ? `${title}: ${v.label}` : v.label}
      sx={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        background: v.color,
        color: "#fff",
        fontSize: "11px",
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
      }}
    >
      {v.icon}
    </Box>
  );
};

/** Color swatch + group name. */
export const GroupLabel = ({ variant }) => (
  <Box
    component="span"
    sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: INK }}
  >
    <Box
      component="span"
      sx={{ width: 10, height: 10, borderRadius: "3px", background: variant.color }}
    />
    {variant.label}
  </Box>
);

const shortLift = (v) => `${v > 0 ? "+" : "−"}${Math.abs(v).toFixed(Math.abs(v) >= 10 ? 0 : 1)}%`;

/**
 * SVG tag beside a point with a clear difference: the lift in white on its
 * verdict color (green better, red worse). Nothing for unclear points.
 * `side` places it above, below, left or right of (x, y).
 */
export const SigLabel = ({ x, y, cmp, side = "above", fontSize = 8.5 }) => {
  if (!cmp || cmp.lift == null || !isClear(cmp.verdict)) return null;
  const text = shortLift(cmp.lift);
  const w = text.length * fontSize * 0.7 + 8;
  const h = fontSize + 5;
  const gap = 5;
  const left =
    side === "right" ? x + gap : side === "left" ? x - gap - w : x - w / 2;
  const top =
    side === "above" ? y - gap - h : side === "below" ? y + gap : y - h / 2;
  return (
    <g pointerEvents="none">
      <rect
        x={left}
        y={top}
        width={w}
        height={h}
        rx={h / 2}
        fill={VERDICTS[cmp.verdict].color}
      />
      <text
        x={left + w / 2}
        y={top + h / 2 + fontSize * 0.35}
        textAnchor="middle"
        fontSize={fontSize}
        fontWeight="700"
        fill="#fff"
      >
        {text}
      </text>
    </g>
  );
};
